import React, { useState } from 'react';
import TradingViewChartIcon from './TradingViewChartIcon';
import './TakeProfitView.css';
import { ensureArray } from './config';

const takeProfitFields = [
  'sRsiAndRsiComboReversal',
  'swingHighCloseSrCrossSignal',
  'swingLowCloseSrCrossSignal',
  'swingHighWickSrCrossSignal',
  'swingLowWickSrCrossSignal',
  'swingLowTakenOut',
  'swingHighTakenOut',
  'swingLowWickTakenOut',
  'swingHighWickTakenOut',
  'ccr',
  'rsiReverse',
  'stochRsiCross',
  'rsiDiv',
  'aoDiv',
  'candlePattern'
];

const getSectionTitle = (field) => ({
  sRsiAndRsiComboReversal: 'Stoch RSI And RSI Combo Reversal',
  swingHighCloseSrCrossSignal: 'Swing High Close SR Cross Signal',
  swingLowCloseSrCrossSignal: 'Swing Low Close SR Cross Signal',
  swingHighWickSrCrossSignal: 'Swing High Wick SR Cross Signal',
  swingLowWickSrCrossSignal: 'Swing Low Wick SR Cross Signal',
  swingLowTakenOut: 'swing Low TakenOut',
  swingHighTakenOut: 'swing High TakenOut',
  swingLowWickTakenOut: 'swing Low Wick Liq Swept',
  swingHighWickTakenOut: 'swing High Wick Liq Swept',
  ccr: 'CCR Signals',
  rsiReverse: 'RSI Reversals',
  stochRsiCross: 'Stoch RSI Cross',
  rsiDiv: 'RSI Div',
  aoDiv: 'AO Div',
  candlePattern: 'Engulfing'
}[field] || field);

const TakeProfitView = ({ data }) => {
  const safeData = ensureArray(data);
  const [globalSymbol, setGlobalSymbol] = useState('');
  const [globalSignalType, setGlobalSignalType] = useState('');

  const allSymbols = [...new Set(safeData.map(item => item.symbol))];
  const hasBull = safeData.some(d => takeProfitFields.some(f => (d[f] || '').toUpperCase().includes('BULL')));
  const hasBear = safeData.some(d => takeProfitFields.some(f => (d[f] || '').toUpperCase().includes('BEAR')));

  const clearGlobalFilters = () => {
    setGlobalSymbol('');
    setGlobalSignalType('');
  };

  return (
    <>
      <div className="tp-global-toolbar">
        <select value={globalSymbol} onChange={(e) => setGlobalSymbol(e.target.value)}>
          <option value="">Select Symbol</option>
          {allSymbols.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <select value={globalSignalType} onChange={(e) => setGlobalSignalType(e.target.value)}>
          <option value="">Select Signal Type</option>
          {hasBull && <option value="BULL">All BULL</option>}
          {hasBear && <option value="BEAR">All BEAR</option>}
        </select>

        <button className="clear-global-btn" onClick={clearGlobalFilters}>Clear Filters</button>
      </div>

      <div className="tp-container">
        {takeProfitFields.map((field) => {
          const fieldData = safeData.filter(item => item[field] && item[field] !== '');

          const filtered = fieldData.filter(item =>
            (!globalSymbol || item.symbol === globalSymbol) &&
            (!globalSignalType || (item[field] || '').includes(globalSignalType))
          );

          if (filtered.length === 0) return null;

          return <TakeProfitSection key={field} field={field} data={filtered} />;
        })}
      </div>
    </>
  );
};

const TakeProfitSection = ({ field, data }) => {
  const safeData = ensureArray(data);
  const [showAll, setShowAll] = useState(false);
  const [filters, setFilters] = useState({ symbol: '', timeFrame: '', signalType: '' });
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });

  const symbols = [...new Set(safeData.map(d => d.symbol))];
  const timeFrames = [...new Set(safeData.map(d => d.timeFrame))];

  const filtered = safeData
    .filter(d => !filters.symbol || d.symbol === filters.symbol)
    .filter(d => !filters.timeFrame || d.timeFrame === +filters.timeFrame)
    .filter(d => !filters.signalType || d[field] === filters.signalType);

  const sorted = [...filtered].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const valA = a[sortConfig.key];
    const valB = b[sortConfig.key];

    if (typeof valA === 'number' && typeof valB === 'number') {
      return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
    }
    return sortConfig.direction === 'asc'
      ? String(valA).localeCompare(String(valB))
      : String(valB).localeCompare(String(valA));
  });

  const displayed = showAll ? sorted : sorted.slice(0, 5);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const uniqueSignals = [...new Set(safeData.map(d => d[field]).filter(Boolean))];

  const norm = (v) => (typeof v === 'string' ? v.toUpperCase() : '');


  const getSignalSLValue = (item, field) => {
  const v = norm(item[field]);

  if (!v) return '';

  if (v.includes('SIGNAL_CLOSE')) return '';

  if (v.includes('BULL')) return item.bullishSignalSL || '';

  if (v.includes('BEAR')) return item.bearishSignalSL || '';

  return '';
};

  return (
    <div className="tp-section">
      <h3>{getSectionTitle(field)}</h3>
      <div className="filters">
        <select onChange={e => setFilters({ ...filters, symbol: e.target.value })} value={filters.symbol}>
          <option value="">Symbol</option>
          {symbols.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select onChange={e => setFilters({ ...filters, timeFrame: e.target.value })} value={filters.timeFrame}>
          <option value="">Time Frame</option>
          {timeFrames.map(t => <option key={t} value={t}>{t} min</option>)}
        </select>
        <select onChange={e => setFilters({ ...filters, signalType: e.target.value })} value={filters.signalType}>
          <option value="">Signal Type</option>
          {uniqueSignals.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        {filtered.length > 5 && (
          <button onClick={() => setShowAll(!showAll)} className="expand-button">
            {showAll ? 'Collapse' : 'Expand'}
          </button>
        )}

        <button onClick={() => setFilters({ symbol: '', timeFrame: '', signalType: '' })}>Clear Filters</button>
      </div>

      <div className="tp-table-wrapper">
        <table className="tp-table">
          <thead>
            <tr>
              <th>#</th>
              <th onClick={() => handleSort('symbol')}>Symbol</th>
              <th>Chart</th>
              <th onClick={() => handleSort('timeFrame')}>Time Frame</th>
              <th onClick={() => handleSort('marketPrice')}>Market Price</th>
              <th onClick={() => handleSort('change')}>Change %</th>
              <th onClick={() => handleSort('volumeRank')}>VolumeRank {sortConfig.key === 'volumeRank' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⬍'}</th>
              <th>Signal SL</th>              
              <th onClick={() => handleSort(field)}>{getSectionTitle(field)}</th>
            </tr>
          </thead>
           <tbody>
            {displayed.map((item, idx) => {
              const value = String(item[field] || '').toUpperCase();
              const isBull = value.includes('BULL');
              const isBear = value.includes('BEAR');
              const rowClass = isBull ? 'bull' : isBear ? 'bear' : '';
              return (
                <tr key={idx} className={rowClass}>
                  <td>{idx + 1}</td>
                  <td className={item.activeNonActive === 'ACTIVE' ? 'highlight-symbol' : ''}>
                    {item.symbol}
                  </td>
                  <td>
                    <TradingViewChartIcon
                      symbol={item.symbol}
                      timeFrame={item.timeFrame}
                      exchange={item.exchange}
                    />
                  </td>
                  <td>{item.timeFrame} min</td>
                  <td>{item.marketPrice}</td>
                  <td>{item.change}</td>
                  <td>{item.volumeRank}</td>   
                  <td>{getSignalSLValue(item, field)}</td> 
                  <td>{item[field]}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TakeProfitView;
